// Loading / "Calculating..." overlay shown after the quiz while the meal plan
// is prepared. Runs for a fixed 3s: caption swaps from the kibble jab to the
// real-food praise at the 1.5s midpoint, and the bottom progress bar animates
// 0→100% over the full duration (see .loading__progress-bar in index.scss).
//
// Returns a Promise that resolves when the overlay is done so the caller can
// await it before revealing the recommendation. `ready` lets the caller pass a
// promise for the plan calculation — the overlay holds until BOTH the 3s timer
// and `ready` have settled, so we never lift onto a half-rendered plan.
const LOADING_DURATION = 4000;
const CAPTION_SWAP_AT = 2000;

// Point the "before" bowl at art matching the step-13 "current__food" answer,
// and swap both overlay captions to the copy for that same answer. Art URLs and
// caption copy come from window.currentFoodData, which quiz-v2.liquid builds
// with asset_url. Falls back to the `default` entry when the question was
// skipped or the answer has no dedicated entry yet, so the overlay always has
// an image and captions.
//
// Exported so index.js can call it the moment the user lands on the email step:
// swapping at handleLoader time is too late, because the browser only starts
// fetching the new src once the overlay is already on screen. Doing it early
// gives the image the whole email-step dwell to load. `root` defaults to the
// document so callers don't need an overlay reference; the call inside
// handleLoader stays as an idempotent safety net.
export const setCurrentFoodImage = (root = document) => {
  const img = root.querySelector("[data-current-food-img]");

  const map = window.currentFoodData;
  if (!map) return;

  const answer = document.querySelector('input[name="current__food"]:checked')?.value;
  const entry = map[answer] || map.default;
  if (!entry) return;

  const fallback = map.default;

  // Captions carry authored <br> markup from the Liquid template, so they go in
  // as HTML. Empty strings are skipped so a half-filled entry keeps the copy
  // already in the markup rather than blanking the caption.
  [1, 2].forEach((n) => {
    const el = root.querySelector(`[data-loading-caption-text="${n}"]`);
    if (!el) return;
    const text = entry[`caption${n}`] || fallback?.[`caption${n}`];
    if (text) el.innerHTML = text;
  });

  if (!img || !entry.src) return;

  // asset_url returns a well-formed URL even for a file that hasn't been
  // uploaded, so a missing asset shows as a broken image rather than an empty
  // src. Swap back to the default bowl if the chosen art fails to load.
  img.onerror = () => {
    img.onerror = null;
    if (fallback?.src && img.getAttribute("src") !== fallback.src) {
      img.setAttribute("src", fallback.src);
      if (fallback.alt) img.setAttribute("alt", fallback.alt);
    }
  };

  if (img.getAttribute("src") !== entry.src) img.setAttribute("src", entry.src);
  if (entry.alt) img.setAttribute("alt", entry.alt);
};

export const handleLoader = (ready = Promise.resolve()) => {
  const overlay = document.querySelector(".loading__overlay");
  if (!overlay) return Promise.resolve();

  const captionKibble = overlay.querySelector('[data-loading-caption="1"]');
  const captionFood = overlay.querySelector('[data-loading-caption="2"]');

  setCurrentFoodImage(overlay);

  // Flag the loading state on the wizard host via data-current="loading" so CSS
  // (and anything else keying off the step) can target it. Stash the real value
  // and restore it when the overlay lifts.
  const wizard = document.querySelector("quiz-wizard") || document.querySelector(".wizard-v2");
  const prevCurrent = wizard?.dataset.current;

  // Restart the progress-bar animation on every run (reflow resets it).
  const bar = overlay.querySelector(".loading__progress-bar");
  if (bar) {
    bar.style.animation = "none";
    void bar.offsetWidth; // force reflow
    bar.style.animation = "";
  }

  overlay.style.display = "flex";
  document.body.classList.add("no-scroll", "is-loading-plan");
  if (wizard) wizard.dataset.current = "loading";

  // Beat 1 → Beat 2 caption swap at the midpoint.
  captionKibble?.classList.add("is-visible");
  captionFood?.classList.remove("is-visible");
  const swapTimer = setTimeout(() => {
    captionKibble?.classList.remove("is-visible");
    captionFood?.classList.add("is-visible");
  }, CAPTION_SWAP_AT);

  const timer = new Promise((resolve) => setTimeout(resolve, LOADING_DURATION));

  return Promise.all([timer, Promise.resolve(ready).catch(() => { })]).then(() => {
    clearTimeout(swapTimer);
    overlay.style.display = "none";
    document.body.classList.remove("no-scroll", "is-loading-plan");
    captionKibble?.classList.remove("is-visible");
    captionFood?.classList.remove("is-visible");
    if (wizard) wizard.dataset.current = prevCurrent ?? "";
  });
};
